import { motion } from "framer-motion";
import { Check, PaintRoller, HardHat } from "lucide-react";
import { useState } from "react";

const painting = [
    "Cabinet painting",
    "Door painting",
    "Trim painting",
    "Interior painting",
    "Exterior painting",
    "Drywall repair",
    "Popcorn ceiling removal",
    "Staining",
    "Epoxy floor coating",
    "Pressure washing",
];

const construction = [
    "Concrete work",
    "Demolition",
    "Drywall installation",
    "Framing",
    "Flooring installation",
    "Tile & ceramic",
    "Roofing",
    "Fan / lighting installation",
    "Window & door installation",
    "Deck & fence installation",
    "Baseboard, molding & remodeling",
];

const ServiceColumn = ({ side, index, hovered, setHovered, title, tag, items, image, Icon }) => {
    const dim = hovered !== null && hovered !== index;
    return (
        <motion.div
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className={`relative border ${side === "left" ? "border-neutral-900/15" : "border-neutral-900/15"} bg-white p-8 md:p-10 transition-all duration-500 ${dim ? "opacity-40" : "opacity-100"}`}
            data-testid={`service-column-${title.toLowerCase()}`}
        >
            <div className="flex items-start justify-between gap-4 mb-8">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{tag}</div>
                    <h3 className="mt-3 font-display text-4xl md:text-5xl font-black tracking-tighter leading-none text-neutral-900">
                        {title}
                    </h3>
                </div>
                <div className="flex items-center justify-center h-14 w-14 rounded-full bg-[color:var(--brand-blue)] text-white">
                    <Icon className="h-6 w-6" />
                </div>
            </div>

            <div className="relative overflow-hidden clip-frame aspect-[4/3] mb-8 bg-neutral-100">
                <motion.img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1 }}
                    animate={{ scale: hovered === index ? 1.06 : 1 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    loading="lazy"
                />
                <div className="absolute inset-0 pointer-events-none border border-white/20" />
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm text-neutral-800">
                        <span className="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[color:var(--brand-blue)]/10 text-[color:var(--brand-blue)]">
                            <Check className="h-3 w-3" />
                        </span>
                        {it}
                    </li>
                ))}
            </ul>
        </motion.div>
    );
};

export default function Services() {
    const [hovered, setHovered] = useState(null);
    return (
        <section id="services" data-testid="services-section" className="relative bg-[color:var(--bg-secondary)] py-24 md:py-40">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">[ Services — 02 ]</div>
                        <h2 className="mt-4 font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.88] text-neutral-900">
                            Professional renovation<br /><span className="text-[color:var(--brand-blue)]">you can trust.</span>
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm md:text-base text-neutral-600 leading-relaxed">
                        From small repairs to full renovations — we do it all. Two disciplines, one crew, one accountable point of contact.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ServiceColumn
                        side="left"
                        index={0}
                        hovered={hovered}
                        setHovered={setHovered}
                        title="Painting"
                        tag="Discipline 01"
                        items={painting}
                        image="https://images.pexels.com/photos/1669754/pexels-photo-1669754.jpeg?auto=compress&cs=tinysrgb&w=1600"
                        Icon={PaintRoller}
                    />
                    <ServiceColumn
                        side="right"
                        index={1}
                        hovered={hovered}
                        setHovered={setHovered}
                        title="Construction"
                        tag="Discipline 02"
                        items={construction}
                        image="https://images.unsplash.com/photo-1646324554833-f0b6a479fa5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                        Icon={HardHat}
                    />
                </div>

                {/* Trust badges */}
                <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {["Experienced", "Reliable", "Affordable", "Licensed & Insured"].map((b, i) => (
                        <motion.div
                            key={b}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08, duration: 0.6 }}
                            className="border border-neutral-900/10 bg-white px-6 py-6 flex items-center gap-3"
                        >
                            <div className="h-2 w-2 rounded-full bg-[color:var(--brand-blue)]" />
                            <span className="font-display font-bold text-sm md:text-base uppercase tracking-tight text-neutral-900">{b}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
